import type { ReactNode } from "react";
import { useIntl } from "@/infra/locale";
import { webshopEnvironment } from "@akina/config";
import type { AppLocale } from "@akina/infra";
import { Dropdown, Icon, Logo, useLocaleStore, Button } from "@akina/presentation";

const LANGUAGE_VALUES: AppLocale[] = ["en-CH", "de"];

type FooterConfig = {
  onBack?: () => void;
  onContinue?: () => void;
  canContinue?: boolean;
};

type WebshopLayoutProps = {
  children: ReactNode;
  footer?: FooterConfig;
  wide?: boolean;
};

export function WebshopLayout({ children, footer, wide }: Readonly<WebshopLayoutProps>) {
  return (
    <div className="bg-offwhite flex h-screen flex-col overflow-hidden">
      <WebshopTopbar />
      <main className="stable-scrollbar flex flex-1 flex-col overflow-y-auto px-6 pt-10 pb-24">
        <div className={`mx-auto w-full ${wide ? "max-w-[1312px]" : "max-w-282"}`}>{children}</div>
      </main>
      <WebshopFooter {...footer} />
    </div>
  );
}

function WebshopTopbar() {
  const setLocale = useLocaleStore(s => s.setLocale);
  const websiteUrl = webshopEnvironment.websiteUrl;
  const { formatMessage } = useIntl();

  const languages = LANGUAGE_VALUES.map(value => ({
    value,
    label: formatMessage({ id: `shared.locale.${value}` }),
  }));

  return (
    <header className="w-full border-b border-gray-800/5 bg-white">
      <div className="mx-auto flex w-full max-w-275 items-center justify-between px-6 py-4">
        <a href={websiteUrl} className="flex items-center" aria-label="Akina">
          <Logo className="h-7 w-auto" />
        </a>
        <Dropdown
          onSelect={opt => setLocale(opt.value)}
          options={languages}
          contentClassName="min-w-0 w-fit py-1"
          itemClassName="px-6"
          trigger={
            <div className="flex cursor-pointer flex-row items-center gap-2 rounded-full px-4 py-2 transition-all duration-75 hover:bg-gray-800/10">
              <Icon type="globe" />
            </div>
          }
        />
      </div>
    </header>
  );
}

function WebshopFooter({ onBack, onContinue, canContinue }: Readonly<FooterConfig>) {
  const { formatMessage } = useIntl();
  return (
    <div className="fixed right-0 bottom-0 left-0 z-10 flex items-center justify-between px-8 py-5">
      <button
        type="button"
        onClick={onBack}
        disabled={!onBack}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-800 bg-transparent transition-colors disabled:opacity-40 enabled:hover:bg-gray-800/10">
        <Icon type="chevronLeft" />
      </button>
      {onContinue ? (
        <Button className="w-auto px-6" onClick={onContinue} disabled={!canContinue} size="medium">
          {formatMessage({ id: "checkoutPage.continueButtonText" })}
        </Button>
      ) : null}
    </div>
  );
}
